import React, { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, MessageCircle } from "lucide-react";
import Button from "src/components/ui/Button";
import Logo from "src/components/common/Logo";
import { SITE_SETTINGS } from "src/constants/common";
import { trackCta, trackNavClick, trackWhatsappClick } from "src/utils/analytics";

const waLink = `https://wa.me/${SITE_SETTINGS.CONTACT_NUMBER.replace(/[^0-9]/g, "")}`;

const Navbar: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const links = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About" },
    { path: "/pricing", label: "Pricing" },
    { path: "/faqs", label: "FAQs" },
    { path: "/contact", label: "Contact" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 bg-white/90 backdrop-blur transition-shadow ${
        scrolled ? "shadow-sm border-b border-gray-100" : "border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto flex items-center justify-between px-6 py-3.5">
        <Link to="/" className="flex items-center gap-2 shrink-0">
          <Logo variant="contained" size="small" />
          <Logo variant="text" size="large" />
        </Link>

        {/* Desktop Nav */}
        <nav className="hidden md:flex items-center gap-1 text-gray-600 font-medium">
          {links.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={() => trackNavClick(l.label, "header")}
              className={`px-3.5 py-2 rounded-lg text-sm transition ${
                location.pathname === l.path
                  ? "text-primary-700 bg-primary-50"
                  : "hover:text-primary-600 hover:bg-gray-50"
              }`}
            >
              {l.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2.5">
          <a
            href={waLink}
            target="_blank"
            rel="noreferrer"
            onClick={() => trackWhatsappClick("header")}
            className="inline-flex items-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2 text-sm font-medium text-emerald-700 transition hover:bg-emerald-100"
          >
            <MessageCircle size={15} />
            WhatsApp us
          </a>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              trackCta("Sign in", "header");
              navigate("/app/login");
            }}
          >
            Sign in
          </Button>
          <Button
            size="sm"
            onClick={() => {
              trackCta("Get Started", "header");
              navigate("/app/register");
            }}
          >
            Get Started
          </Button>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setOpen(!open)}
          className="md:hidden text-gray-700 focus:outline-none"
          aria-label={open ? "Close menu" : "Open menu"}
        >
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      {open && (
        <div className="md:hidden bg-white border-t border-gray-100">
          {links.map((l) => (
            <Link
              key={l.path}
              to={l.path}
              onClick={() => {
                trackNavClick(l.label, "mobile_menu");
                setOpen(false);
              }}
              className={`block px-6 py-3 text-gray-700 hover:bg-primary-50 hover:text-primary-600 ${
                location.pathname === l.path
                  ? "bg-primary-50 text-primary-600"
                  : ""
              }`}
            >
              {l.label}
            </Link>
          ))}
          <div className="flex flex-col gap-2.5 px-6 py-4">
            <a
              href={waLink}
              target="_blank"
              rel="noreferrer"
              onClick={() => trackWhatsappClick("mobile_menu")}
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-emerald-200 bg-emerald-50 px-3.5 py-2.5 text-sm font-medium text-emerald-700"
            >
              <MessageCircle size={15} />
              WhatsApp us
            </a>
            <div className="flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => {
                  trackCta("Sign in", "mobile_menu");
                  navigate("/app/login");
                }}
                fullWidth
              >
                Sign in
              </Button>
              <Button
                onClick={() => {
                  trackCta("Get Started", "mobile_menu");
                  navigate("/app/register");
                }}
                fullWidth
              >
                Get Started
              </Button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
